import React from "react";
import { type BrandColor } from "contexts/color-theme";
import { type ImageProps } from "next/image";
import { type ReactNode } from "react";
import { type Icon, Box, type LayoutProps } from "@chakra-ui/react";
import { Superscript } from "ui/text";
import { type InsightsAnimationProps } from "components/animations";

export type CdpIndustryUseCase = {
  icon: typeof Icon;
  title: string;
  description: string;
};

export type CdpIndustryFeatureConsideration = {
  traditional: string;
  composable: string;
};

export type FeaturedOutcome = {
  color: BrandColor;
  icon: typeof Icon;
  title: string;
};

type CustomerDataStat = {
  title: string;
  description: ReactNode;
  href: string;
};

type PersonalizationCards = {
  title: string;
  href: string;
};

export const personalizedExperiencesStat: CustomerDataStat = {
  title: "56%",
  description: (
    <>
      of customers expect
      <br /> personalized experiences
    </>
  ),
  href: "https://segment.com/pdfs/State-of-Personalization-Report-Twilio-Segment-2023.pdf",
};

export const dataBreachStat: CustomerDataStat = {
  title: "$3.86M",
  description: (
    <>
      average cost
      <br />
      of a data breach
    </>
  ),
  href: "https://www.ibm.com/security/digital-assets/cost-data-breach-report/1Cost%20of%20a%20Data%20Breach%20Report%202020.pdf",
};

export const revenueReferenceCopy = (
  <>
    With{" "}
    <Box as="strong" fontWeight="bold">
      80% of revenue coming from just 20% of customers,
      <Superscript size="sm" href="https://asana.com/resources/pareto-principle-80-20-rule" text="1" /> customer loyalty
      is everything.
    </Box>{" "}
    Data is your only advantage to stand out in a sea of virtually endless options.
  </>
);

export type CdpIndustryConfig = {
  /** An icon to represent the industry, shown on the hub catalog. */
  icon: typeof Icon;
  /** The lowercase industry name, that can be adjusted for title case when necessary. E.g., "retail" or "financial services". */
  name: string;
  /** An optional shorter name to use throughout the page copy, using the longer name for the hero and meta information. */
  abbreviatedName?: string;
  /** Optional title-cased name to use when special capitalization or punctuation is included. E.g., for "Technology & B2B SaaS". */
  titleCaseName?: string;
  /** A plural noun to refer to members of the industry, e.g. "retailers" or "healthcare companies". */
  pluralNoun: string;
  /** If the industry has a corresponding solution page (e.g. /solutions/retail), use this to load in the same set of customer logos. */
  solutionPageSlug?: string;
  /** Sets the color theme for the entire page template. */
  color: Exclude<BrandColor, "gray">;
  /** Copy to be included as hidden text on the page for SEO purposes. */
  seoCopy: string;
  heroImage: ImageProps;
  adoptingCdpsDescription: ReactNode;
  featuredOutcomes: [FeaturedOutcome, FeaturedOutcome, FeaturedOutcome];
  dataStatsTitleMaxW?: LayoutProps["maxW"];
  dataStats: [CustomerDataStat, CustomerDataStat, CustomerDataStat];
  personalizationCards: [PersonalizationCards, PersonalizationCards, PersonalizationCards];
  useCases: {
    performanceMarketing: CdpIndustryUseCase[];
    lifecycleMarketing: CdpIndustryUseCase[];
    onsitePersonalization: CdpIndustryUseCase[];
  };
  considerations: {
    dataModeling: CdpIndustryFeatureConsideration;
    audienceManagement: CdpIndustryFeatureConsideration;
    customerJourneys: CdpIndustryFeatureConsideration;
    identityResolution: CdpIndustryFeatureConsideration;
  };
  whyHightouchTitleMaxW?: LayoutProps["maxW"];
  /** Optional props that can be used to adjust the copy within the insights animation of the CDP features accordion. */
  audienceOverlap?: Pick<InsightsAnimationProps, "title" | "firstCategoryLabel" | "secondCategoryLabel">;
};
